import React, { useState } from 'react';
import {
  FaUser, FaEnvelope, FaLock, FaEye, FaEyeSlash, FaSave,
  FaShieldAlt, FaMagic, FaCopy, FaCheck,
} from 'react-icons/fa';
import { supabase } from '../../config/supabaseConfig.ts';
import { useAuth } from '../../context/AuthContext';
import Input from '../../components/Input';
import Button from '../../components/Button';
import { toastSuccess, toastError } from '../../utils/toast';

/* ── Generador de contraseña segura con crypto.getRandomValues ── */
const UPPER   = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const LOWER   = 'abcdefghijklmnopqrstuvwxyz';
const DIGITS  = '0123456789';
const SPECIAL = '!@#$%&*()-_=+[]{}|;:,.<>?';
const ALL     = UPPER + LOWER + DIGITS + SPECIAL;

const randomIndex = (max) => {
  const arr = new Uint32Array(1);
  crypto.getRandomValues(arr);
  return arr[0] % max;
};

const generatePassword = (length = 18) => {
  const required = [
    UPPER[randomIndex(UPPER.length)],
    LOWER[randomIndex(LOWER.length)],
    DIGITS[randomIndex(DIGITS.length)],
    SPECIAL[randomIndex(SPECIAL.length)],
  ];
  const rest = Array.from({ length: length - 4 }, () => ALL[randomIndex(ALL.length)]);
  const all = [...required, ...rest];
  // Fisher-Yates shuffle con crypto
  for (let i = all.length - 1; i > 0; i--) {
    const j = randomIndex(i + 1);
    [all[i], all[j]] = [all[j], all[i]];
  }
  return all.join('');
};

/* ── Fuerza visual de la contraseña ── */
const getStrength = (p) => {
  if (!p) return null;
  let score = 0;
  if (p.length >= 8)  score++;
  if (p.length >= 14) score++;
  if (/[A-Z]/.test(p)) score++;
  if (/[0-9]/.test(p)) score++;
  if (/[^A-Za-z0-9]/.test(p)) score++;
  if (score <= 2) return { label: 'Débil',   color: '#F87171', pct: 33  };
  if (score <= 3) return { label: 'Regular', color: '#FBBF24', pct: 66  };
  return             { label: 'Fuerte',  color: '#4ADE80', pct: 100 };
};

const SectionTitle = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: '#4A5270' }}>
    {children}
  </p>
);

const ProfilePage = () => {
  const { currentUser } = useAuth();

  const [info, setInfo] = useState({
    full_name: currentUser?.full_name || '',
    email: currentUser?.email || '',
  });
  const [infoLoading, setInfoLoading] = useState(false);

  const [pwd, setPwd] = useState({ newPassword: '', confirmPassword: '' });
  const [show, setShow] = useState({ new: false, confirm: false });
  const [pwdLoading, setPwdLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null); // contraseña sugerida visible
  const [copied, setCopied] = useState(false);

  const toggleShow = (field) => setShow(s => ({ ...s, [field]: !s[field] }));

  /* Genera y aplica sugerencia */
  const applySuggestion = () => {
    const p = generatePassword();
    setSuggestion(p);
    setPwd({ newPassword: p, confirmPassword: p });
    setShow({ new: true, confirm: true });
    setCopied(false);
  };

  const copyToClipboard = () => {
    if (!suggestion) return;
    navigator.clipboard.writeText(suggestion).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const dismissSuggestion = () => setSuggestion(null);

  /* ── Guardar nombre y/o email ── */
  const handleSaveInfo = async (e) => {
    e.preventDefault();
    if (!info.full_name.trim()) { toastError('El nombre no puede estar vacío'); return; }
    if (!info.email.trim())     { toastError('El correo no puede estar vacío'); return; }

    setInfoLoading(true);
    try {
      // Nombre: actualiza user_metadata (no requiere email)
      const { error: nameError } = await supabase.auth.updateUser({
        data: { full_name: info.full_name },
      });
      if (nameError) throw nameError;

      // Email: operación separada, solo si cambió
      if (info.email !== currentUser.email) {
        const { error: emailError } = await supabase.auth.updateUser({ email: info.email });
        if (emailError) {
          toastSuccess('Nombre actualizado');
          toastError('Cambio de correo falló: configura SMTP o desactiva "Confirm email" en Supabase Auth');
          return;
        }
      }

      toastSuccess('Información actualizada');
    } catch (err) {
      toastError(err.message || 'Error al actualizar la información');
    } finally {
      setInfoLoading(false);
    }
  };

  /* ── Cambiar contraseña ── */
  const handleSavePassword = async (e) => {
    e.preventDefault();
    if (!pwd.newPassword)                        { toastError('Ingresa una contraseña'); return; }
    if (pwd.newPassword.length < 8)              { toastError('Mínimo 8 caracteres'); return; }
    if (pwd.newPassword !== pwd.confirmPassword) { toastError('Las contraseñas no coinciden'); return; }

    setPwdLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pwd.newPassword });
      if (error) throw error;
      toastSuccess('Contraseña actualizada');
      setPwd({ newPassword: '', confirmPassword: '' });
      setSuggestion(null);
    } catch (err) {
      toastError(err.message || 'Error al cambiar la contraseña');
    } finally {
      setPwdLoading(false);
    }
  };

  const EyeBtn = ({ field }) => (
    <button type="button" onClick={() => toggleShow(field)} tabIndex={-1}
      style={{ color: '#4A5270', background: 'none', border: 'none', cursor: 'pointer', display: 'flex' }}>
      {show[field] ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
    </button>
  );

  const strength = getStrength(pwd.newPassword);

  return (
    <div className="max-w-2xl mx-auto space-y-6">

      {/* ── Información personal ── */}
      <div
        className="rounded-xl p-6"
        style={{ backgroundColor: '#12132a', border: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="flex items-center gap-4 mb-6">
          <div
            className="h-14 w-14 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0 select-none"
            style={{ backgroundColor: '#DC1F26', boxShadow: '0 0 18px rgba(220,31,38,0.4)' }}
          >
            {info.full_name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <p className="text-base font-semibold" style={{ color: '#E8EAF0' }}>{info.full_name || '—'}</p>
            <p className="text-xs mt-0.5" style={{ color: '#4A5270' }}>Administrador</p>
          </div>
        </div>

        <SectionTitle>Información personal</SectionTitle>

        <form onSubmit={handleSaveInfo} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Nombre completo" name="full_name" type="text" size="lg"
              value={info.full_name} leftIcon={<FaUser />}
              onChange={(e) => setInfo(i => ({ ...i, full_name: e.target.value }))}
            />
            <Input
              label="Correo electrónico" name="email" type="email" size="lg"
              value={info.email} leftIcon={<FaEnvelope />}
              onChange={(e) => setInfo(i => ({ ...i, email: e.target.value }))}
            />
          </div>

          <div className="flex justify-end">
            <Button type="submit" variant="primary" size="sm"
              leftIcon={<FaSave size={11} />} disabled={infoLoading}>
              {infoLoading ? 'Guardando…' : 'Guardar información'}
            </Button>
          </div>
        </form>
      </div>

      {/* ── Cambio de contraseña ── */}
      <div
        className="rounded-xl p-6"
        style={{ backgroundColor: '#12132a', border: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <FaShieldAlt size={12} style={{ color: '#4A5270' }} />
            <SectionTitle>Cambiar contraseña</SectionTitle>
          </div>

          {/* Botón sugerir contraseña */}
          <button
            type="button"
            onClick={applySuggestion}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-150"
            style={{
              backgroundColor: 'rgba(167,139,250,0.1)',
              border: '1px solid rgba(167,139,250,0.25)',
              color: '#A78BFA',
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(167,139,250,0.18)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(167,139,250,0.1)'}
          >
            <FaMagic size={10} />
            Sugerir contraseña segura
          </button>
        </div>

        <p className="text-xs mb-5" style={{ color: '#3E4460' }}>
          Mínimo 8 caracteres. Combina mayúsculas, números y símbolos.
        </p>

        {/* Caja de contraseña sugerida */}
        {suggestion && (
          <div
            className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl mb-4"
            style={{
              backgroundColor: 'rgba(167,139,250,0.07)',
              border: '1px solid rgba(167,139,250,0.2)',
            }}
          >
            <div className="min-w-0">
              <p className="text-[10px] font-semibold mb-1" style={{ color: '#7C6FBF' }}>
                Contraseña sugerida — ya aplicada en los campos
              </p>
              <p
                className="text-sm font-mono font-semibold tracking-wider truncate select-all"
                style={{ color: '#C4B5FD' }}
              >
                {suggestion}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={copyToClipboard}
                title="Copiar al portapapeles"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-all duration-150"
                style={{
                  backgroundColor: copied ? 'rgba(74,222,128,0.12)' : 'rgba(167,139,250,0.15)',
                  color: copied ? '#4ADE80' : '#A78BFA',
                  border: `1px solid ${copied ? 'rgba(74,222,128,0.3)' : 'rgba(167,139,250,0.25)'}`,
                }}
              >
                {copied ? <FaCheck size={11} /> : <FaCopy size={11} />}
                {copied ? 'Copiada' : 'Copiar'}
              </button>
              <button
                type="button"
                onClick={dismissSuggestion}
                title="Descartar sugerencia"
                className="text-xs px-2 py-1.5 rounded-lg transition-colors"
                style={{ color: '#3E4460', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSavePassword} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Input
                label="Nueva contraseña" name="newPassword"
                type={show.new ? 'text' : 'password'} size="lg"
                value={pwd.newPassword} leftIcon={<FaLock />}
                rightIcon={<EyeBtn field="new" />}
                onChange={(e) => {
                  setPwd(p => ({ ...p, newPassword: e.target.value }));
                  setSuggestion(null);
                }}
              />
              {/* Barra de fuerza */}
              {strength && (
                <div className="mt-2 px-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px]" style={{ color: '#4A5270' }}>Fortaleza</span>
                    <span className="text-[10px] font-medium" style={{ color: strength.color }}>
                      {strength.label}
                    </span>
                  </div>
                  <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{ width: `${strength.pct}%`, backgroundColor: strength.color }}
                    />
                  </div>
                </div>
              )}
            </div>
            <Input
              label="Confirmar contraseña" name="confirmPassword"
              type={show.confirm ? 'text' : 'password'} size="lg"
              value={pwd.confirmPassword} leftIcon={<FaLock />}
              rightIcon={<EyeBtn field="confirm" />}
              onChange={(e) => {
                setPwd(p => ({ ...p, confirmPassword: e.target.value }));
                setSuggestion(null);
              }}
            />
          </div>

          <div className="flex justify-end">
            <Button type="submit" variant="primary" size="sm"
              leftIcon={<FaSave size={11} />} disabled={pwdLoading}>
              {pwdLoading ? 'Guardando…' : 'Cambiar contraseña'}
            </Button>
          </div>
        </form>
      </div>

    </div>
  );
};

export default ProfilePage;
